/**
 * Wave 14 Agent TRIAL - Layer 3.
 *
 * NPS modal fired on Day 7 of the trial. 0-10 picker + optional
 * comment. Submits a POST to /wave14/trial/milestones/track with a
 * metadata blob (we keep the survey storage opportunistic; the
 * authoritative NPS store can be added later without changing this
 * surface).
 */
import { useEffect, useState } from 'react'
import { useTrialStatus } from '@/hooks/useTrialStatus'
import { wave14Trial } from '@/api/wave14Trial'

const STORAGE_KEY = 'wave14_nps_submitted'
const NPS_DAY_TRIGGER = 7

export function NPSSurveyModal() {
  const { status, daysElapsed } = useTrialStatus()
  const [open, setOpen] = useState(false)
  const [score, setScore] = useState<number | null>(null)
  const [comment, setComment] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)

  useEffect(() => {
    if (!status || !status.in_trial) return
    try {
      if (localStorage.getItem(STORAGE_KEY)) return
    } catch {
      /* noop */
    }
    if (daysElapsed >= NPS_DAY_TRIGGER) setOpen(true)
  }, [status, daysElapsed])

  if (!open || done) return null

  const submit = async () => {
    if (score === null) return
    setSubmitting(true)
    try {
      await wave14Trial.track(
        'schedule_demo_with_sales',
        `nps:${score}|comment:${comment.slice(0, 500)}`
      )
    } catch {
      /* swallow */
    } finally {
      try {
        localStorage.setItem(STORAGE_KEY, String(score))
      } catch {
        /* noop */
      }
      setDone(true)
      setSubmitting(false)
      setOpen(false)
    }
  }

  const close = () => {
    try {
      localStorage.setItem(STORAGE_KEY, 'declined')
    } catch {
      /* noop */
    }
    setOpen(false)
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="nps-survey-modal"
      className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center"
    >
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex justify-between items-start">
          <h3 className="text-lg font-semibold">How likely are you to recommend ORKSTRA?</h3>
          <button onClick={close} aria-label="Dismiss" className="text-slate-500">×</button>
        </div>
        <p className="text-sm text-slate-600 mt-1">
          0 = not at all, 10 = absolutely. Takes 10 seconds.
        </p>
        <div className="grid grid-cols-11 gap-1 mt-4">
          {Array.from({ length: 11 }, (_, i) => i).map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setScore(n)}
              className={`text-sm py-2 rounded border ${
                score === n
                  ? 'bg-blue-600 text-white border-blue-700'
                  : 'border-slate-200 hover:bg-slate-50'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="(Optional) What's the single biggest thing we could improve?"
          rows={3}
          className="mt-4 w-full rounded border border-slate-200 p-2 text-sm"
        />
        <div className="flex justify-end gap-2 mt-3">
          <button onClick={close} className="px-3 py-1 text-sm">
            Skip
          </button>
          <button
            onClick={submit}
            disabled={score === null || submitting}
            className="px-3 py-1 rounded bg-blue-600 text-white text-sm disabled:opacity-40"
          >
            {submitting ? 'Sending…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default NPSSurveyModal
